import _ from "lodash/fp";
import Papa from "papaparse";

import {
  dataSourceOptions,
  reciprocalRelationshipTypes,
  relationshipTypeOptions,
} from "../../Workspace/MetadataCard/constants";
import {
  lettersArray,
  isPassageNameValid,
  uniqueObjects,
} from "../../../services/utils";
import { lightweightItemFields } from "../../../stores/item";

const CHUNKSIZE = 25;
const RETURNTODEFAULT = "ReturnToDefault";
const REQUIREDITEMFIELDS = [
  "Content Area",
  "Content Specialist",
  "Item Format",
];
const REQUIREDPASSAGEFIELDS = ["Content Area", "Content Specialist"];
const STANDARDORDERS = ["Primary", "Secondary", "Tertiary"];
const INVALIDSTATES = [
  "Stimulus Review",
  "Peer Review 1",
  "Peer Review 2",
  "Peer Review 3",
  "External Review",
];

const constructObjectFromRow =
  ({
    existingObject,
    metadataFields,
    passageID,
    program,
    relationshipID,
    type,
    users,
  }) =>
  (row) => {
    const { configurationID } = program;
    return Object.keys(row).reduce(
      (acc, fieldName) => {
        const value = row[fieldName];
        if (!value) return { ...acc };

        switch (fieldName) {
          case `Relationship Data Source`:
          case `Relationship Type`:
          case `Standard Order`:
            return { ...acc };
          case `${_.capitalize(type)} Name`:
            return {
              ...acc,
              name: value,
            };
          case `Correct Answer`:
            return {
              ...acc,
              correctAnswer: value,
            };
          case `Foils`:
            return {
              ...acc,
              foils: parseInt(value),
            };
          case `Passage`:
            return {
              ...acc,
              passage: passageID,
            };
          case `Related ${_.capitalize(type)} Name`:
            return {
              ...acc,
              relationships:
                value === RETURNTODEFAULT
                  ? null
                  : [
                      {
                        id: relationshipID,
                        name: value,
                        source: row[`Relationship Data Source`].toUpperCase(),
                        type: convertRelationshipType(row[`Relationship Type`]),
                      },
                    ],
            };
          case `Standard`:
            return {
              ...acc,
              standards:
                value === RETURNTODEFAULT
                  ? row[`Standard Order`].toLowerCase() || null
                  : [
                      {
                        humanCodingScheme: value,
                        order: row[`Standard Order`].toLowerCase(),
                      },
                    ],
            };
          default:
            const metadataField = metadataFields.find(
              ({ label }) => label === fieldName,
            );
            if (!metadataField) return { ...acc };

            const path = metadataField.path;
            if (value === RETURNTODEFAULT) return { ...acc, [path]: null };
            switch (metadataField.type) {
              case "date":
                return { ...acc, [path]: convertDate(value) };
              case "integer":
                return { ...acc, [path]: parseInt(value) };
              case "multipleSelect":
                return {
                  ...acc,
                  [path]: getMultipleSelectValue({
                    configurationID,
                    metadataField,
                  })(value),
                };
              case "select":
                return {
                  ...acc,
                  [path]: getSelectValue({
                    configurationID,
                    metadataField,
                  })(value),
                };
              case "switch":
                return { ...acc, [path]: /^yes$/i.test(value) };
              case "user":
                const id = users.find(({ name }) => name === value)?.id;
                return { ...acc, [path]: { id, name: value } };
              default:
                return { ...acc, [path]: value };
            }
        }
      },
      { id: existingObject?.id },
    );
  };

const convertDate = (date) => {
  let convertedDate = new Date(date);
  return convertedDate.toString() === "Invalid Date"
    ? null
    : `${convertedDate.getFullYear()}-${(
        "0" +
        (convertedDate.getMonth() + 1)
      ).slice(-2)}-${("0" + convertedDate.getDate()).slice(-2)}`;
};

const convertRelationshipType = (type) =>
  type.replace(/-|\s/g, "_").toUpperCase();

const doItemNamesExist = (client) => async (names) => {
  // If there are no names being retrieved, return early
  if (!names.length) return [];

  return client
    .query(
      getDoItemNamesExistQuery,
      {
        names,
      },
      { requestPolicy: "network-only" },
    )
    .toPromise()
    .then((response) => {
      if (response.error) throw new Error(response.error);
      return response.data?.doItemNamesExist;
    });
};

const doPassageNamesExist = (client) => async (names) => {
  // If there are no names being retrieved, return early
  if (!names.length) return [];

  return client
    .query(
      getDoPassageNamesExistQuery,
      {
        names,
      },
      { requestPolicy: "network-only" },
    )
    .toPromise()
    .then((response) => {
      if (response.error) throw new Error(response.error);
      return response.data?.doPassageNamesExist;
    });
};

const objectFields = `
  contentSpecialist {
    id
    name
  }
  developmentState
  id
  itemWriter {
    id
    name
  }
  name
  program
  versionID
`;

const failedUpdatedObjectFields = ` 
  id
`;

const generatePayloads =
  ({ metadataFields, program, type, users }) =>
  (csvObjects) => {
    const [objects, reciprocalRelationships] = csvObjects.reduce(
      ([objAcc, relAcc], object) => {
        const { data: row, existingObject, passageID, relationshipID } = object;
        const update = constructObjectFromRow({
          existingObject,
          metadataFields,
          passageID,
          program,
          relationshipID,
          type,
          users,
        })(row);
        const recipRels = update.relationships?.length
          ? getReciprocalRelationships({ ...relAcc })(update.relationships)
          : { ...relAcc };
        const index = objAcc.findIndex(
          ({ id, name }) =>
            (!!existingObject?.id && existingObject.id === id) ||
            (type === "passage" && update.name === name),
        );
        const currentObject = index > -1 ? objAcc[index] : existingObject || {};
        const updatedObject =
          _.assignWith(objectCombiner)(currentObject)(update);

        if (index > -1) {
          return [
            [
              ...objAcc.slice(0, index),
              { ...updatedObject },
              ...objAcc.slice(index + 1),
            ],
            { ...recipRels },
          ];
        }
        return [[...objAcc, { ...updatedObject }], { ...recipRels }];
      },
      [[], {}],
    );
    return (
      objects
        // Update choiceInteractions/correctAnswer when appropriate
        .map(updateChoiceInteractions(type))
        // Update relationships when appropriate
        .map(updateRelationships(reciprocalRelationships))
    );
  };

const getAction =
  ({ existingObjectNamesInProgram, type }) =>
  (row) => {
    const objectName = row[`${_.capitalize(type)} Name`];
    return (type === "item" && !objectName) ||
      (type === "passage" && !existingObjectNamesInProgram.includes(objectName))
      ? "Create"
      : "Update";
  };

const getBatchItemUploadMutation = `
  mutation BatchItemUpload($itemsToCreate: [ItemInput!], $itemIDsToUpdate: [ID!], $itemsToUpdate: [ItemInput!], $program: ID!) {
    batchItemUpload(itemsToCreate: $itemsToCreate, itemIDsToUpdate: $itemIDsToUpdate, itemsToUpdate: $itemsToUpdate, program: $program) {
      itemsCreated { ${objectFields} }
      itemsUpdated { ${objectFields} }
      failedUpdated { ${failedUpdatedObjectFields} }
    }
  }`;

const getBatchPassageUploadMutation = `
  mutation BatchPassageUpload($passagesToCreate: [PassageInput!], $passageIDsToUpdate: [ID!], $passagesToUpdate: [PassageInput!], $program: ID!) {
    batchPassageUpload(passagesToCreate: $passagesToCreate, passageIDsToUpdate: $passageIDsToUpdate, passagesToUpdate: $passagesToUpdate, program: $program) {
      passagesCreated { ${objectFields} }
      passagesUpdated { ${objectFields} }
    }
  }`;

const getDoItemNamesExistQuery = `
  query ItemNameExists($names: [String!]!) {
    doItemNamesExist(names: $names) {
      found
      name
    }
  }`;

const getDoPassageNamesExistQuery = `
  query PassageNameExists($names: [String!]!) {
    doPassageNamesExist(names: $names) {
      found
      name
    }
  }`;

const getExistingObject =
  ({ action, existingObjectNamesInProgram, existingObjectsInProgram, type }) =>
  (row) => {
    const objectName = row[`${_.capitalize(type)} Name`];
    if (
      action !== "Update" ||
      !existingObjectNamesInProgram.includes(objectName)
    )
      return null;

    // Remove _actions from the object and program from each relationship
    const { _actions, items, relationships, ...object } =
      existingObjectsInProgram.find(({ name }) => name === objectName);
    return removeTypenameFromObject({
      ...object,
      items,
      relationships:
        relationships?.map(({ program, ...relationship }) => relationship) ||
        relationships,
    });
  };

const getInvalidFields =
  ({
    action,
    allPassages,
    contentSpecialistExist,
    existingObject,
    existingObjectNamesInProgram,
    existingRelatedObjectsInProgram,
    metadataFields,
    passageID,
    program,
    standards,
    type,
    users,
  }) =>
  (row) =>
    Object.keys(row).filter((fieldName) => {
      const value = row[fieldName];
      // Get the metadata configuration for the field
      const field = metadataFields.find(({ label }) => label === fieldName);
      switch (fieldName) {
        case `${_.capitalize(type)} Name`:
          if (type === "passage") {
            // Should be marked invalid if:
            // - The value is falsy, OR
            // - The value is not valid, OR
            // - The value exists in the system, but not in the given program
            return (
              !value ||
              (!allPassages.some(
                ({ found, name }) => found && value === name,
              ) &&
                !isPassageNameValid(value)) ||
              (allPassages.some(({ found, name }) => found && value === name) &&
                !existingObjectNamesInProgram.includes(value))
            );
          }
          if (type === "item") {
            // Should be marked invalid if:
            // - The value is truthy, AND
            // - The value does not exist in the given program
            return !!value && !existingObjectNamesInProgram.includes(value);
          }
          break;
        case "Correct Answer":
          const foilCount = existingObject
            ? existingObject.choiceInteractions.at(0)?.foils.length
            : parseInt(row[`Foils`]) || 4;
          return (
            // Correct Answer is not a valid column for Passage BDU
            type === "passage" ||
            // Must be truthy in order to be marked invalid
            (!!value &&
              // Should be marked invalid if:
              // - Item Format is not Selected Response, OR
              // - The value wouldn't match one of the foil letters
              ((action === "Create" &&
                row[`Item Format`] !== "Selected Response") ||
                (action === "Update" &&
                  existingObject[`Item Format`] !== "Selected Response") ||
                !lettersArray.slice(0, foilCount).includes(value)))
          );
        case "Foils":
          return (
            // Foils is not a valid column for Passage BDU
            type === "passage" ||
            // Must be truthy in order to be marked invalid
            (!!value &&
              // Should be marked invalid if:
              // - The item is not being created, OR
              // - Item Format is not Selected Response, OR
              // - The value doesn't parse to an integer
              (action !== "Create" ||
                row[`Item Format`] !== "Selected Response" ||
                !parseInt(value)))
          );
        case "Passage":
          return (
            // Passage is not a valid column for Passage BDU
            type === "passage" ||
            // Must be truthy in order to be marked invalid
            (!!value &&
              // Should be marked invalid if:
              // - A passage with the given name does not exist in the program
              !passageID)
          );
        case `Related ${_.capitalize(type)} Name`:
          return (
            // Must be truthy in order to be marked invalid
            !!value &&
            // Should be marked invalid if:
            // - The other Relationship fields are empty, OR
            // - The value is not ReturnToDefault AND the CDE-sourced value was not found in the system
            (!row[`Relationship Type`] ||
              !row[`Relationship Data Source`] ||
              (value !== RETURNTODEFAULT &&
                row[`Relationship Data Source`] === "CDE" &&
                !existingRelatedObjectsInProgram.some(
                  ({ name }) => name === value,
                )))
          );
        case `Relationship Data Source`:
          return (
            // Must be truthy in order to be marked invalid
            !!value &&
            // Should be marked invalid if:
            // - The other Relationship fields are empty, OR
            // - The value is not a valid option
            (!row[`Related ${_.capitalize(type)} Name`] ||
              !row[`Relationship Type`] ||
              !dataSourceOptions.includes(value))
          );
        case `Relationship Type`:
          return (
            // Must be truthy in order to be marked invalid
            !!value &&
            // Should be marked invalid if:
            // - The other Relationship fields are empty, OR
            // - The value is not a valid option
            (!row[`Related ${_.capitalize(type)} Name`] ||
              !row[`Relationship Data Source`] ||
              !relationshipTypeOptions.includes(value))
          );
        case `Standard`:
          return (
            // Must be truthy in order to be marked invalid
            !!value &&
            // Should be marked invalid if:
            // - The value is not ReturnToDefault AND the value is not a valid option
            value !== RETURNTODEFAULT &&
            !standards.some(
              ({ humanCodingScheme }) => humanCodingScheme === value,
            )
          );
        case `Standard Order`:
          return (
            // Must be truthy in order to be marked invalid
            !!value &&
            // Should be marked invalid if:
            // - The other Standards fields are empty, OR
            // - The value is not a valid option
            (!row[`Standard`] || !STANDARDORDERS.includes(value))
          );
        case `Development State`:
          return (
            // The metadata configuration must define the field
            !field ||
            // Must be truthy in order to be marked invalid
            (!!value &&
              // Should be marked invalid if:
              // - The value is not valid for the field/program
              !isValueValid({ field, program, type, users })(value)) ||
            INVALIDSTATES.includes(value) ||
            !contentSpecialistExist
          );
        default:
          return (
            // The metadata configuration must define the field
            !field ||
            // Must be truthy in order to be marked invalid
            (!!value &&
              // Should be marked invalid if:
              // - The value is not valid for the field/program
              !isValueValid({ field, program, type, users })(value))
          );
      }
    });

const getItemsByNameQuery = `
  query searchItemsByList($itemList: [String!]!, $program: ID) {
    itemsByList(itemList: $itemList, program: $program) { ${lightweightItemFields} }
  }`;

const getItemsByName =
  (client) =>
  ({ itemList, program }) => {
    if (!itemList?.length) return [];

    return client
      .query(
        getItemsByNameQuery,
        {
          itemList,
          program,
        },
        { requestPolicy: "network-only" },
      )
      .toPromise()
      .then((response) => {
        if (response.error) throw new Error(response.error);
        return response.data?.itemsByList;
      });
  };

const getMissingRequiredFields =
  ({ action, type }) =>
  (row) => {
    const GROUPEDFIELDS = [
      [
        `Related ${_.capitalize(type)} Name`,
        `Relationship Data Source`,
        `Relationship Type`,
      ],
    ];
    const missingGroupedFields = GROUPEDFIELDS.map((fields) =>
      fields.some((fieldName) => row[fieldName]) &&
      !fields.every((fieldName) => row[fieldName])
        ? fields.filter((fieldName) => !row[fieldName])
        : [],
    ).flat();
    if (action === "Update") return [...missingGroupedFields];
    if (type === "item") {
      return [
        ...missingGroupedFields,
        ...REQUIREDITEMFIELDS.filter((fieldName) => !row[fieldName]),
      ];
    }
    if (type === "passage") {
      return [
        ...missingGroupedFields,
        ...REQUIREDPASSAGEFIELDS.filter((fieldName) => !row[fieldName]),
      ];
    }
  };

const getMultipleSelectValue =
  ({ configurationID, metadataField }) =>
  (value) =>
    value
      .split(/,\s*/)
      .map(getSelectValue({ configurationID, metadataField }))
      .join(",");

const getObjectsByName =
  ({ client, program, type }) =>
  (names) => {
    // If there are no names being retrieved, return early
    if (!names.length) return [];

    // If we're retrieving items
    if (type === "item") {
      return getItemsByName(client)({
        itemList: names,
        program: program?.id,
      });
    }
    // If we're retrieving passages
    if (type === "passage") {
      return getPassagesByName(client)({
        passageList: names,
        program: program?.id,
      });
    }
  };

const getPassageID =
  ({
    existingAssociatedObjectNamesInProgram,
    existingAssociatedObjectsInProgram,
    type,
  }) =>
  (row) =>
    type === "item" &&
    !!row[`Passage`] &&
    existingAssociatedObjectNamesInProgram.includes(row[`Passage`])
      ? existingAssociatedObjectsInProgram.find(
          ({ name }) => name === row[`Passage`],
        ).id
      : null;

const PassageListFields = `
      _actions
      aiContent
      application
      argumentativePassageSet
      cbtCharacterCount
      cdeID
      cognitiveCode
      contentArea
      contentSpecialist {
        id
        name
      }
      contentLicensed
      copyrightCertificate
      copyrightDate
      createdDateTime
      createdByUser {
        id
        name
      }
      crossCurriculum
      developmentState     
      elementStatus
      ethnicity
      externalID
      firstBankedDateTime
      fleschKincaid
      foreignID
      gender
      genderRole1
      genderRole2
      genderRole3
      gradeLevel
      gradeSpanEnd
      gradeSpanStart
      graphicAssets {
        fileTimestamp
        name
        user
      }
      groupRole1
      groupRole2
      groupRole3
      hasUnresolvedChangeTags
      id
      importID
      income
      items
      itemWriter {
        id
        name
      }
      keywords
      language
      lastBankedDateTime
      lastModifiedDateTime
      lastModifiedByUser {
        id
        name
      }
      name
      pairedPassage
      passageAsAWhole
      pbtCharacterCount
      pointOfView
      presentationFormat
      primaryContentCode
      program
      readabilityIndex
      readingLevelNotes   
      relationships {
        id
        name
        program
        source
        type
      }
      region 
      release
      religion
      resource
      restriction
      secondaryContentCode
      secondaryType
      secondarySubType
      shortPassageSet
      standards {
        humanCodingScheme
        order
      }
      subEthnicity
      subProgram
      subRegion
      subType
      summary
      textComplexity
      topic
      type
      unavailability
      unitStrength
      universalDesign
      urbanicity
      useScreenReader
      useTextToSpeech
      versionID
      vqiPassageSet
      width
      wordCount
    `;

const getPassagesByNameQuery = `
  query searchPassagesByList($passageList: [String!]!, $program: ID) {
    passagesByList(passageList: $passageList, program: $program) { ${PassageListFields} }
  }`;

const getPassagesByName =
  (client) =>
  ({ passageList, program }) => {
    if (!passageList?.length) return [];

    return client
      .query(
        getPassagesByNameQuery,
        {
          passageList,
          program,
        },
        { requestPolicy: "network-only" },
      )
      .toPromise()
      .then((response) => {
        if (response.error) throw new Error(response.error);
        return response.data?.passagesByList;
      });
  };

const getReciprocalRelationships =
  (reciprocalRelationships) =>
  ({ id, name, relationships }) => {
    const relationship = relationships?.at(0);
    if (!relationship) return { ...reciprocalRelationships };

    const key = Object.keys(reciprocalRelationshipTypes).find(
      (type) => convertRelationshipType(type) === relationship.type,
    );
    const reciprocalRelationshipType = reciprocalRelationshipTypes[key];

    if (relationship.source !== "CDE" || !reciprocalRelationshipType)
      return { ...reciprocalRelationships };
    return {
      ...reciprocalRelationships,
      [relationship.name]: [
        ...(reciprocalRelationships[relationship.name] || []),
        {
          id,
          name,
          source: "CDE",
          type: convertRelationshipType(reciprocalRelationshipType),
        },
      ],
    };
  };

const getRelationshipID =
  ({
    existingRelatedObjectNamesInProgram,
    existingRelatedObjectsInProgram,
    type,
  }) =>
  (row) => {
    const value = row[`Related ${_.capitalize(type)} Name`];
    const source = row[`Relationship Data Source`];

    return existingRelatedObjectNamesInProgram.includes(value) &&
      source === "CDE"
      ? existingRelatedObjectsInProgram.find(({ name }) => name === value).id
      : null;
  };

const getSelectValue =
  ({ configurationID, metadataField }) =>
  (value) => {
    const option = metadataField.values
      .filter(({ configurations }) => configurations.includes(configurationID))
      .find(({ booleanValue, intValue, stringValue }) => {
        if (typeof booleanValue !== "undefined" && booleanValue !== null)
          return booleanValue === /^yes$/i.test(value);
        if (typeof intValue !== "undefined" && intValue !== null)
          return intValue === parseInt(value);
        if (typeof stringValue !== "undefined" && stringValue !== null)
          return stringValue === value;
      });
    if (!option) return null;
    const key = ["booleanValue", "intValue", "stringValue"].find(
      (s) => typeof option[s] !== "undefined" && option[s] !== null,
    );
    return option[key];
  };

const getUpdatedFields =
  ({ action, existingObject, metadataFields, type, users }) =>
  (row) => {
    if (action === "Create" || !existingObject) return [];
    return Object.keys(row).filter((fieldName) => {
      // Get the metadata configuration for the field
      const field = metadataFields.find(({ label }) => label === fieldName);

      // Get the current/submitted values for the field
      const currentValue = field ? existingObject[field.path] : null;
      const value = row[fieldName];
      switch (fieldName) {
        case `${_.capitalize(type)} Name`:
        case "Foils":
        case "Passage":
          return false;
        case "Correct Answer":
          return !!value;
        case "Content Licensed":
          return field.values.some(
            ({ booleanValue }) =>
              booleanValue !== null &&
              (currentValue ? /^no$/i.test(value) : /^yes$/i.test(value)),
          );
        case `Related ${_.capitalize(type)} Name`:
        case `Relationship Data Source`:
          return (
            !!value &&
            !existingObject.relationships?.some(
              ({ name, source }) =>
                row[`Related ${_.capitalize(type)} Name`] === name &&
                row[`Relationship Data Source`].toUpperCase() ===
                  source.toUpperCase(),
            )
          );
        case `Relationship Type`:
          return (
            !!value &&
            existingObject.relationships
              ?.find(
                ({ name, source }) =>
                  row[`Related ${_.capitalize(type)} Name`] === name &&
                  row[`Relationship Data Source`].toUpperCase() ===
                    source.toUpperCase(),
              )
              ?.type.toUpperCase() !== value.toUpperCase()
          );
        case `Standard`:
          return (
            !!value &&
            !existingObject.standards?.some(
              ({ humanCodingScheme }) => value === humanCodingScheme,
            )
          );
        case `Standard Order`:
          return (
            !!value &&
            existingObject.standards?.find(
              ({ humanCodingScheme }) => row[`Standard`] === humanCodingScheme,
            )?.order !== value
          );
        default:
          return (
            !!value &&
            (!currentValue ||
              value === RETURNTODEFAULT ||
              (field.type === "date" && convertDate(value) !== currentValue) ||
              (field.type === "integer" && parseInt(value) !== currentValue) ||
              (field.type === "multipleSelect" &&
                isMultipleSelectUpdated(field)({ currentValue, value })) ||
              (field.type === "select" &&
                isSelectUpdated(field)({ currentValue, value })) ||
              (field.type === "switch" &&
                field.values.some(
                  ({ booleanValue }) =>
                    booleanValue !== null &&
                    (currentValue ? /^yes$/i.test(value) : /^no$/i.test(value)),
                )) ||
              (field.type === "text" && value !== currentValue) ||
              (field.type === "user" &&
                value !==
                  users.find(({ id }) => id === currentValue?.id)?.name))
          );
      }
    });
  };

const checkFailedRequestData = (sentRequestData) => (receivedRequestData) => {
  const { itemsCreated } = receivedRequestData;

  const sentCreatedDataCount = sentRequestData.filter(
    (item) => item.action === "Create",
  ).length;

  return [...sentRequestData].map((row) => {
    if (row.action === "Create") {
      if (
        sentCreatedDataCount > 0 &&
        itemsCreated?.length < sentCreatedDataCount
      ) {
        row.requestFailedFields = Object.keys(row.data);
      }
    }
    if (row.action === "Update") {
      if (
        receivedRequestData.failedUpdated?.some(
          (item) => item.id === row.existingObject.id,
        )
      ) {
        row.requestFailedFields = Object.keys(row.data);
      }
    }

    return { ...row };
  });
};

const hideUpdatedFields =
  ({ type, uploadedData }) =>
  (savedData) => {
    const keys = {
      Create: {
        developmentState: "Development State",
        id: "id",
        name: `${_.capitalize(type)} Name`,
      },
      Update: {
        developmentState: "Development State",
      },
    };

    const createdObjects = uploadedData.filter(({ id }) => !id);
    const updatedObjects = uploadedData.filter(({ id }) => !!id);

    return [...uploadedData].map((row) => {
      let index;
      if (row.action === "Create") {
        index = createdObjects.findIndex((o) => o === row);
      }
      if (row.action === "Update") {
        index = updatedObjects.findIndex((o) => o === row);
      }
      if (index === -1) return { ...row };
      return Object.keys(keys[row.action]).reduce((acc, savedKey) => {
        const savedRow = savedData[`${type}s${row.action}d`].at(index);
        if (!savedRow?.[savedKey]) return { ...acc };
        const uploadedKey = keys[row.action][savedKey];
        if (savedRow[savedKey] === row.data[uploadedKey]) return { ...acc };
        return {
          ...acc,
          data: {
            ...acc.data,
            [uploadedKey]: savedRow[savedKey],
          },
          updatedFields: (acc.updatedFields || []).filter(
            (s) => s !== uploadedKey,
          ),
        };
      }, row);
    });
  };

const parseCSV = async (file) => {
  return new Promise((resolve, reject) => {
    Papa.parse(file, {
      header: true,
      skipEmptyLines: "greedy",
      complete(results) {
        resolve(results);
      },
      error(err) {
        reject(err);
      },
    });
  });
};

const isDateValid = (date) => !!convertDate(date);

const isIntegerValid =
  ({ constraints }) =>
  (integer) =>
    Number.isInteger(parseInt(integer)) &&
    (!constraints.maxLength ||
      parseInt(integer) < parseInt("9".repeat(constraints.maxLength)));

const isMultipleSelectUpdated =
  (field) =>
  ({ currentValue, value }) => {
    const currentElements = currentValue || [];
    const elements = value?.split(/,\s*/).filter(Boolean) || [];
    if (currentElements.length !== elements.length) return true;

    return currentElements.some((val) =>
      field.values.some(
        ({ booleanValue, intValue, stringValue }) =>
          (booleanValue !== null &&
            booleanValue === val &&
            !elements.some((element) =>
              val ? /^yes$/i.test(element) : /^no$/i.test(element),
            )) ||
          (intValue !== null &&
            intValue === val &&
            !elements.includes(val.toString())) ||
          (stringValue !== null &&
            stringValue === val &&
            !elements.includes(val)),
      ),
    );
  };

const isSelectUpdated =
  (field) =>
  ({ currentValue, value }) =>
    field.values.some(
      ({ booleanValue, intValue, stringValue }) =>
        (booleanValue !== null &&
          booleanValue === currentValue &&
          value !==
            (currentValue ? /^yes$/i.test(value) : /^no$/i.test(value))) ||
        (intValue !== null &&
          intValue === currentValue &&
          value !== currentValue.toString()) ||
        (stringValue !== null &&
          stringValue === currentValue &&
          value !== currentValue),
    );

const isSelectValueValid =
  ({ field, program }) =>
  (value) =>
    field.values.some(
      ({ booleanValue, configurations, intValue, stringValue }) =>
        configurations.includes(program.configurationID) &&
        ((booleanValue !== null &&
          [/^no$/i, /^yes$/i].some((re) => re.test(value))) ||
          (intValue && intValue.toString() === value) ||
          (stringValue && stringValue === value)),
    );

const isValueValid =
  ({ field, program, type, users }) =>
  (value) => {
    const constraints = field.constraints.find(({ configurations }) =>
      configurations.includes(program.configurationID),
    );
    return (
      // If the program's configuration includes the given field, AND
      constraints &&
      // The value is "ReturnToDefault" and the field is not required, OR
      ((value === RETURNTODEFAULT &&
        ((type === "item" && !REQUIREDITEMFIELDS.includes(field.label)) ||
          (type === "passage" &&
            !REQUIREDPASSAGEFIELDS.includes(field.label)))) ||
        // If the field is a "date" field and the date is valid, OR
        (field.type === "date" && isDateValid(value)) ||
        // If the field is an "integer" field and the value parses to an integer, OR
        (field.type === "integer" && isIntegerValid({ constraints })(value)) ||
        // If the field is a "multipleSelect" field and the value matches one or more of the possible options
        (field.type === "multipleSelect" &&
          value.split(/,\s*/g).every(isSelectValueValid({ field, program }))) ||
        // If the field is a "select" field and the value matches one of the possible options
        (field.type === "select" &&
          isSelectValueValid({ field, program })(value)) ||
        // If the field is a "switch" field and the value is a boolean
        (field.type === "switch" &&
          [/^no$/i, /^yes$/i].some((re) => re.test(value))) ||
        // If the field is a "text" field and the value is less than the maxLength
        (field.type === "text" &&
          (!constraints.maxLength || value.length <= constraints.maxLength)) ||
        // If the field is a "user" field and the given user has the correct roles/permissions
        (field.type === "user" &&
          field.values.some(
            ({ configurations, roles, scope }) =>
              configurations.includes(program.configurationID) &&
              roles?.length &&
              roles.includes(field.label) &&
              users.some(
                ({ name, programAccess }) =>
                  name === value &&
                  programAccess.some(
                    (pa) =>
                      pa.program === program.id &&
                      pa.reviewTypes.includes(field.label),
                  ),
              ),
          )))
    );
  };

const objectCombiner = (objValue, srcValue, key) => {
  switch (key) {
    case "relationships":
      // If the new object has a null value, reset the array
      if (srcValue === null) return [];
      // If there are relationships in both arrays
      if (objValue?.length && srcValue?.length) {
        return uniqueObjects(
          [...srcValue, ...objValue],
          ["id", "name", "source", "type"],
        );
      }
      // If there are relationships in the old array, return them
      if (objValue?.length) return [...objValue];
      // If there are relationships in the new array, return them
      if (srcValue?.length) return [...srcValue];
      // Otherwise, return an empty array
      return [];
    case "standards":
      // If the new object has a null value, reset the array
      if (srcValue === null) return [];
      // If the new object has a string value, filter out any standards with different order values
      if (typeof srcValue === "string")
        return objValue.filter(({ order }) => order !== srcValue);
      // If there are standards in both arrays, concat the arrays
      if (objValue?.length && srcValue?.length)
        return [...objValue, ...srcValue];
      // If there are standards in the old array, return them
      if (objValue?.length) return [...objValue];
      // If there are standards in the new array, return them
      if (srcValue?.length) return [...srcValue];
      // Otherwise, return an empty array
      return [];
    default:
      return _.isUndefined(srcValue) ? objValue : srcValue;
  }
};

const removeTypenameFromObject = (object) => {
  if (!_.isPlainObject(object)) return object;
  return Object.entries(object).reduce((acc, [key, value]) => {
    if (key === "__typename") return { ...acc };
    if (Array.isArray(value))
      return { ...acc, [key]: value.map(removeTypenameFromObject) };
    return {
      ...acc,
      [key]: _.isPlainObject(value) ? removeTypenameFromObject(value) : value,
    };
  }, {});
};

const saveBatchItemUpload =
  (client) =>
  ({ itemsToCreate, itemIDsToUpdate, itemsToUpdate, program }) =>
    client
      .mutation(
        getBatchItemUploadMutation,
        {
          itemsToCreate,
          itemIDsToUpdate,
          itemsToUpdate,
          program,
        },
        { requestPolicy: "network-only" },
      )
      .toPromise()
      .then((response) => {
        if (response.error) throw new Error(response.error);
        return response.data?.batchItemUpload;
      });

const saveBatchPassageUpload =
  (client) =>
  ({ passagesToCreate, passageIDsToUpdate, passagesToUpdate, program }) =>
    client
      .mutation(
        getBatchPassageUploadMutation,
        {
          passagesToCreate,
          passageIDsToUpdate,
          passagesToUpdate,
          program,
        },
        { requestPolicy: "network-only" },
      )
      .toPromise()
      .then((response) => {
        if (response.error) throw new Error(response.error);
        return response.data?.batchPassageUpload;
      });

const saveData =
  ({ client, metadataFields, program, type, users }) =>
  async (csvObjects) => {
    const promises = _.pipe([
      generatePayloads({
        metadataFields,
        program,
        type,
        users,
      }),
      _.chunk(CHUNKSIZE),
      _.map((objects) => {
        const objectsToCreate = objects.filter(({ id }) => !id);
        const objectsToUpdate = objects.filter(({ id }) => id);
        const objectIDsToUpdate = objectsToUpdate.map(({ id }) => id);
        if (type === "item") {
          return saveBatchItemUpload(client)({
            itemIDsToUpdate: objectIDsToUpdate,
            itemsToCreate: objectsToCreate,
            itemsToUpdate: objectsToUpdate,
            program: program.id,
          });
        }
        if (type === "passage") {
          return saveBatchPassageUpload(client)({
            passageIDsToUpdate: objectIDsToUpdate,
            passagesToCreate: objectsToCreate,
            passagesToUpdate: objectsToUpdate,
            program: program.id,
          });
        }
      }),
    ])([...csvObjects]);
    const responses = await Promise.allSettled(promises);
    const createdKey = `${type}sCreated`;
    const updatedKey = `${type}sUpdated`;
    const failedUpdatedKey = "failedUpdated";

    return responses
      .filter(({ status }) => status === "fulfilled")
      .reduce(
        (acc, response) => ({
          [createdKey]: [
            ...acc[createdKey],
            ...(response.value?.[createdKey] || []),
          ],
          [updatedKey]: [
            ...acc[updatedKey],
            ...(response.value?.[updatedKey] || []),
          ],
          [failedUpdatedKey]: [
            ...acc[failedUpdatedKey],
            ...(response.value?.[failedUpdatedKey] || []),
          ],
        }),
        {
          [createdKey]: [],
          [updatedKey]: [],
          [failedUpdatedKey]: [],
        },
      );
  };

const updateChoiceInteractions = (type) => (object) => {
  // If we are not updating an item with a correct answer, do nothing
  if (type !== "item" || object.action !== "Update" || !object.correctAnswer)
    return { ...object };

  const choiceInteractions = object.choiceInteractions.map((interaction) => ({
    ...interaction,
    foils: interaction.foils.map((foil, index) => ({
      ...foil,
      correct: object.correctAnswer === lettersArray[index],
    })),
  }));
  return {
    ...object,
    choiceInteractions,
    correctAnswer: undefined,
  };
};

const updateRelationships = (reciprocalRelationships) => (object) => {
  const relationships = _.uniqWith(
    (arrVal, othVal) =>
      (arrVal.source === "CDE" &&
        arrVal.source === othVal.source &&
        arrVal.name === othVal.name &&
        arrVal.id === othVal.id) ||
      (arrVal.source !== "CDE" &&
        arrVal.source === othVal.source &&
        arrVal.name === othVal.name &&
        arrVal.type === othVal.type),
  )([
    ...(object.relationships || []),
    ...(reciprocalRelationships[object.name] || []).filter(
      (reciprocalRelationship) =>
        object.relationships.some(
          (relationship) => relationship.name === reciprocalRelationship.name,
        ),
    ),
  ]);
  return {
    ...object,
    relationships,
  };
};

const validateCSV =
  ({ metadataFields, type }) =>
  (parsedCSV) => {
    const {
      data,
      meta: { fields },
    } = parsedCSV;
    if (!fields.length) return { message: "The uploaded CSV is empty" };
    if (!data.length)
      return {
        message:
          "The uploaded CSV contains either fields or rows, but not both",
      };

    const allowedFields = [
      ...metadataFields.map(({ label }) => label),
      `${_.capitalize(type)} Name`,
      type === "item" && "Correct Answer",
      type === "item" && "Foils",
      type === "item" && "Passage",
      `Related ${_.capitalize(type)} Name`,
      "Relationship Data Source",
      "Relationship Type",
      "Standard",
      "Standard Order",
    ].filter(Boolean);
    const disallowedFields = ["CBT Char Count", "PBT Char Count"];

    return {
      invalidFields: fields.filter(
        (field) =>
          // It's explicitly not allowed (even if it might otherwise be allowed)
          disallowedFields.includes(field) ||
          // It's explicitly allowed
          !allowedFields.includes(field),
      ),
    };
  };

const validateData =
  ({ client, metadataFields, program, standards, type, users }) =>
  async (rawCSVRows) => {
    // Remove empty rows
    const rows = rawCSVRows.filter((o) => Object.keys(o).length);

    const objectNames = rows
      .map((o) => o[`${_.capitalize(type)} Name`])
      .filter(Boolean);
    const relatedObjectNames = rows
      .map((o) =>
        o["Relationship Data Source"] === "CDE"
          ? o[`Related ${_.capitalize(type)} Name`]
          : null,
      )
      .filter(Boolean);
    const associatedObjectNames =
      type === "passage" ? [] : rows.map((o) => o[`Passage`]).filter(Boolean);

    const associatedNames = _.uniqWith(_.isEqual)(
      rows
        .filter(
          (row) =>
            relatedObjectNames.includes(
              row[`Related ${_.capitalize(type)} Name`],
            ) && row["Relationship Data Source"] === "CDE",
        )
        .map((apn) => {
          return {
            [`${_.capitalize(type)} Name`]:
              apn[`Related ${_.capitalize(type)} Name`],
            [`Related ${_.capitalize(type)} Name`]:
              apn[`${_.capitalize(type)} Name`],
            [`Relationship Data Source`]: apn["Relationship Data Source"],
            [`Relationship Type`]: convertRelationshipType(
              reciprocalRelationshipTypes[apn["Relationship Type"]],
            ),
          };
        }),
    );

    const [
      existingObjectsInProgram,
      existingRelatedObjectsInProgram,
      existingAssociatedObjectsInProgram,
      allPassages,
    ] = await Promise.all([
      // Get item/passage names
      getObjectsByName({ client, program, type })(objectNames),
      // Get related item/passage server data (if 'CDE' data source)
      getObjectsByName({ client, type })(relatedObjectNames),
      // Get associated passage data for items
      getObjectsByName({ client, program, type: "passage" })(
        associatedObjectNames,
      ),
      // Get a list of all passages and whether they are found in the system
      doPassageNamesExist(client)(
        type === "item" ? associatedObjectNames : objectNames,
      ),
    ]);

    const existingObjectNamesInProgram = existingObjectsInProgram.map(
      (o) => o.name,
    );
    const existingRelatedObjectNamesInProgram =
      existingRelatedObjectsInProgram.map((o) => o.name);
    const existingAssociatedObjectNamesInProgram =
      existingAssociatedObjectsInProgram.map((o) => o.name);

    return rows.reduce((acc, row) => {
      // Determine whether the row is creating or updating an object
      const action = getAction({
        existingObjectNamesInProgram,
        type,
      })(row);

      // Get the object if it already exists in the system
      let existingObject = getExistingObject({
        action,
        existingObjectNamesInProgram,
        existingObjectsInProgram,
        type,
      })(row);

      const contentSpecialistExist =
        existingObject?.contentSpecialist || action !== "Update" ? true : false;

      if (relatedObjectNames.includes(existingObject?.name)) {
        const associatedObjects = _.uniqWith(_.isEqual)(
          associatedNames
            .map((apn) => {
              const object = existingObjectsInProgram.find(
                (eo) =>
                  eo.name === apn[`Related ${_.capitalize(type)} Name`] &&
                  existingObject.name === apn[`${_.capitalize(type)} Name`],
              );
              return {
                id: object.id,
                name: apn[`Related ${_.capitalize(type)} Name`],
                source: apn["Relationship Data Source"],
                type: apn["Relationship Type"],
              };
            })
            .flat(),
        );
        existingObject = {
          ...existingObject,
          relationships: existingObject?.relationships?.length
            ? associatedObjects.concat(existingObject.relationships).reduce(
                (acc, relationship) => {
                  if (relationship.source !== "CDE") {
                    acc.result.push(relationship);
                  } else {
                    const uniqueKey = `${relationship.id}-${relationship.name}`;

                    if (!acc.keys.has(uniqueKey)) {
                      acc.keys.add(uniqueKey);
                      acc.result.push(relationship);
                    }
                  }

                  return acc;
                },
                { keys: new Set(), result: [] },
              ).result
            : associatedObjects,
        };
      }

      // Get an item's associated passage ID
      const passageID = getPassageID({
        existingAssociatedObjectNamesInProgram,
        existingAssociatedObjectsInProgram,
        type,
      })(row);

      // Get the ID of an existing, matching relationship (if one exists)
      const relationshipID = getRelationshipID({
        existingRelatedObjectNamesInProgram,
        existingRelatedObjectsInProgram,
        type,
      })(row);

      // Get a list of fields with invalid values
      const invalidFields = getInvalidFields({
        action,
        allPassages,
        contentSpecialistExist,
        existingObjectNamesInProgram,
        existingRelatedObjectsInProgram,
        metadataFields,
        passageID,
        program,
        relationshipID,
        standards,
        type,
        users,
      })(row);

      // Get a list of fields that are required, but missing
      const missingRequiredFields = getMissingRequiredFields({ action, type })(
        row,
      );

      // Get a list of fields being updated (that differ from existing data)
      const updatedFields = getUpdatedFields({
        action,
        existingObject,
        metadataFields,
        type,
        users,
      })(row);

      return [
        ...acc,
        {
          action,
          data: { ...row },
          existingObject,
          invalidFields,
          missingRequiredFields,
          passageID,
          relationshipID,
          updatedFields,
        },
      ];
    }, []);
  };

export {
  doItemNamesExist,
  doPassageNamesExist,
  getBatchPassageUploadMutation,
  getDoItemNamesExistQuery,
  getDoPassageNamesExistQuery,
  getInvalidFields,
  getItemsByName,
  getItemsByNameQuery,
  getObjectsByName,
  getPassagesByName,
  getPassagesByNameQuery,
  getUpdatedFields,
  hideUpdatedFields,
  parseCSV,
  removeTypenameFromObject,
  saveData,
  validateCSV,
  validateData,
  checkFailedRequestData,
};
